import { Component, NgZone, inject, OnInit, effect } from '@angular/core';
import { MonitoringService } from 'src/app/core/services/monitoring';
import { AnalyticsRecorder, AnalyticsService } from 'src/app/core/services/analytics';
import { AuditLogService } from 'src/app/core/services/audit-log';
import { AccountState } from 'src/app/store/account';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AuthService, AuthState } from 'src/app/store/auth';
import { GlobalNavBarComponent } from './layout/global-nav-bar/global-nav-bar.component';
import { StaffAccessBannerComponent } from './layout/staff-access-banner/staff-access-banner.component';
import { DomainJoinPromptComponent } from 'src/app/layout/domain-join-prompt/domain-join-prompt.component';
import { BottomSheetComponent, ActionDialogComponent } from './ui-kit';
import { NgTemplateOutlet } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { NavigationState } from 'src/app/store/navigation/navigation.state';
import { DeviceState } from './store/device/device.state';
import { CommandPaletteComponent } from './layout/command-palette/command-palette.component';
import { ResponsiveModalBackdropComponent } from './ui-kit/responsive-modal/responsive-modal-backdrop.component';
import { CommandPaletteService } from './store/command-palette';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { UserInitService } from 'src/app/core/services/user-init.service';
import { environment } from 'src/environments/environment';
/**
 * Root App Component
 *
 * Handles application initialization, including:
 * - UI state initialization (theme, preferences)
 * - Auth state initialization (restore session)
 * - Signal-based states (AccountState auto-syncs with Firebase Auth)
 * - Initial navigation for authenticated users
 * - Navigation bars visibility management
 * - Global bottom sheet management
 * - Global backdrop management
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    NgTemplateOutlet,
    IonApp,
    IonRouterOutlet,
    GlobalNavBarComponent,
    StaffAccessBannerComponent,
    BottomSheetComponent,
    DomainJoinPromptComponent,
    ActionDialogComponent,
    CommandPaletteComponent,
    ResponsiveModalBackdropComponent,
  ],
  animations: [
    trigger('backdrop', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly deviceState = inject(DeviceState);
  private readonly accountState = inject(AccountState);
  private readonly monitoringService = inject(MonitoringService);
  private readonly analytics = inject(AnalyticsService);
  private readonly analyticsRecorder = inject(AnalyticsRecorder);
  private readonly auditLog = inject(AuditLogService);
  private readonly authState = inject(AuthState);
  /** The `uid|district` Session Started last fired for; reset on sign-out. */
  private sessionTrackedFor: string | null = null;
  private readonly zone = inject(NgZone);
  private readonly userInitService = inject(UserInitService);

  /** Navigation state (replaces NavigationBarsState + NavigationBarsService) */
  readonly nav = inject(NavigationState);

  /** Command palette service */
  readonly commandPaletteService = inject(CommandPaletteService);

  /** Hide bottom global nav on routes that set `data.hideGlobalNavBar` (e.g. live workshop). */
  readonly hideGlobalNavBar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.leafRouteData()['hideGlobalNavBar'] === true),
      startWith(this.leafRouteData()['hideGlobalNavBar'] === true),
    ),
    { initialValue: this.leafRouteData()['hideGlobalNavBar'] === true },
  );

  // Navigation bar visibility (signal reads)
  readonly bottomNavBarVisible = this.nav.bottomNavVisible;

  // Action dialog (signal reads)
  readonly actionDialog = this.nav.actionDialog;
  readonly actionDialogVisible = this.nav.actionDialogVisible;

  // Backdrop (signal read)
  readonly backdrop = this.nav.backdrop;

  // Safety banner — visible only when the app is wired to local emulators.
  readonly emulatorModeActive = environment.emulator === true;
  readonly emulatorProjectId = environment.firebase.projectId;

  constructor() {
    // Initialize apple app site association
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://blumemethod.app/tabs/tab2
        const slug = event.url.split('.app').pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
    // Re-identifies on every team switch as well as on sign-in, so the
    // district traits always describe the district the person is working in.
    // LogRocket's Total Sessions group-bys and the dashboard filter bar read
    // user traits, not event properties, so the district has to be here too.
    effect(() => {
      const uid = this.accountState.uid();
      if (!uid) return;
      const team = this.accountState.activeTeam();
      this.monitoringService.identify(uid, {
        email: this.accountState.email() ?? '',
        name: this.accountState.fullName() ?? '',
        role: this.accountState.activeRole() ?? '',
        team_id: team?.id ?? '',
        district: team?.name ?? '',
      });
    });
    // Shared context for every custom event: the district, role and licence
    // window only, never anything about a student. Kept separate from
    // identify so it updates on every team switch, not only when the uid
    // changes.
    effect(() => {
      const team = this.accountState.activeTeam();
      this.monitoringService.setContext({
        team_id: team?.id ?? null,
        district: team?.name ?? null,
        role: this.accountState.activeRole() ?? null,
        license_days_remaining: team?._licenseDaysRemaining ?? null,
        is_trial: team?._isOnTrial ?? null,
      });

      // Blume's own analytics record against the Firebase Auth uid (the rules
      // compare it to the caller) in the active district. Set before Session
      // Started below so that event is never dropped. Blume staff visiting a
      // district are excluded: they are not its members.
      const authUid = this.authState.userId();
      this.analyticsRecorder.setActor(
        authUid && team?.id
          ? {
              uid: authUid,
              partnerId: team.id,
              role: this.accountState.activeRole() ?? null,
              joinedAt: this.accountState.activeProfile()?.inviteAcceptedAt ?? null,
              excluded: this.accountState._isVerifiedBlumeAdmin(),
            }
          : null,
      );

      // Session Started: once per signed-in app load, and again when the
      // person switches district (a session in that district), fired here so
      // it always carries the context set just above. Waits for the account to
      // finish loading so the district is known; a user with no district still
      // gets one. Signing out re-arms it for the next sign-in.
      const uid = this.accountState.uid();
      if (!uid) {
        this.sessionTrackedFor = null;
        return;
      }
      const sessionKey = `${uid}|${team?.id ?? ''}`;
      if (this.sessionTrackedFor === sessionKey || !this.accountState.initialized()) return;
      this.sessionTrackedFor = sessionKey;
      this.analytics.track('Session Started');
    });

    // Who the audit log records as acting: the Firebase Auth uid and token
    // email (the rules require both to match the caller), the display name
    // and role as at the action, and the active district for district
    // entries. Cleared on sign-out.
    effect(() => {
      const uid = this.authState.userId();
      const email = this.authState.userEmail()?.toLowerCase();
      if (!uid || !email) {
        this.auditLog.setActor(null);
        return;
      }
      this.auditLog.setActor({
        uid,
        email,
        label: this.accountState.fullName() || null,
        role: this.accountState.activeRole() ?? null,
        partnerId: this.accountState.activeTeam()?.id ?? null,
      });
    });
  }

  ngOnInit(): void {
    // Reset init flag so users go through /loading on every page refresh
    this.userInitService.reset();
    // Initialize Auth state - loads saved session and sets up Firebase listener
    this.authService.initialize();
  }

  closeActionDialog(): void {
    this.nav.closeActionDialog();
  }

  onBackdropClick(): void {
    this.nav.hideBackdrop();
  }

  closeCommandPalette(): void {
    this.commandPaletteService.close();
  }

  onCommandPaletteSearchChange(searchValue: string): void {
    this.commandPaletteService.setSearchValue(searchValue);
  }

  private leafRouteData(): Record<string, unknown> {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data as Record<string, unknown>;
  }
}
